<template>
    <modal
        id="limit-validity-start-modal"
        :centered="true"
        :title="$t('shop.validityRangeLimitedTitle')"
        :show="modal.show"
        :close="modal.close"
        :closeable="modal.closeable"
        @modal-invisible="successfulCompleted = modal.show = modal.closeable = modal.close = false"
        size="modal-lg"
    >
        <template v-if="!successfulCompleted">
            <div class="modal-body">
                <p v-html="$t('shop.validityRangeLimitedText', placeholders)"></p>

                <div class="form-group">
                    <label for="validityRangeLimitedEmail" v-html="$t('shop.validityRangeLimitedEmail')"></label>
                    <input
                        type="email"
                        name="validityRangeLimitedEmail"
                        class="form-control"
                        id="validityRangeLimitedEmail"
                        :placeholder="$t('shop.email_placeholder')"
                        :class="{'is-invalid': formErrors.email}"
                        v-model="form.email"
                        required
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.email" :key="error">
                            {{ error }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="modal-footer justify-content-center">
                <button
                    type="button"
                    class="btn btn-secondary"
                    :disabled="isOngoing"
                    @click="onCallback(false)"
                    v-html="$t('shop.validityRangeLimitedClose')"
                ></button>
                <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="isOngoing"
                    @click="onCallback(true)"
                    v-html="$t('shop.validityRangeLimitedRememberMe')"
                ></button>
            </div>

            <loading-animation
                :visible="isOngoing"
                :always-show="false"
                preloader-container-id=""
                status-container-id=""
                :loading-text="$t('shop.validityRangeLimitedRememberMeOngoing')"
            ></loading-animation>
        </template>
        <template v-else>
            <div class="modal-body">
                <div class="alert alert-success"
                     v-html="$t('shop.validityRangeLimitedReminderCreated', placeholders)"></div>
            </div>
            <div class="modal-footer justify-content-center">
                <button
                    type="button"
                    class="btn btn-primary"
                    @click="onCallback(false)"
                    v-html="$t('generic.close')"
                ></button>
            </div>
        </template>
    </modal>
</template>

<script>
export default {
    name: 'LimitValidityModal',

    props: {
        settings: {
            type: Object,
            required: true,
        },
        customerEmail: {
            type: String,
            required: true,
        },
        placeholders: {
            type: Object,
            required: true,
        },
        productType: {
            type: String,
            required: true,
        },
        optionId: {
            type: Number,
            required: false,
        },
    },

    data() {
        return {
            modal: {
                show: false,
                close: false,
                closeable: false,
            },
            successfulCompleted: false,
            isOngoing: false,
            formErrors: {},
            form: {
                placeholders: this.placeholders,
                email: this.customerEmail,
            },
        };
    },

    watch: {
        /**
         * Watches the customer-email param to set it at the form if necessary.
         *
         * @returns {void}
         */
        customerEmail() {
            if (this.form.email !== '' && this.form.email.substring(0, 1) !== this.customerEmail.substring(0, 1)) {
                return;
            }

            this.form.email = this.customerEmail;
        },

        /**
         * @returns {void}
         */
        placeholders() {
            this.form.placeholders = this.placeholders;
        },
    },

    computed: {
        /**
         * @returns {number}
         */
        limitValidityRangePicker() {
            return this.limitValidityStart ? this.settings['shop.limitValidityRangePicker'] : 0;
        },
    },

    methods: {
        /**
         * Handle the visibility change of the modal.
         *
         * @returns {void}
         */
        showModal() {
            this.modal.closeable = this.modal.close = false;
            this.modal.show = true;
        },

        /**
         * Handle the hiding from the visible modal.
         *
         * @returns {void}
         */
        hideModal() {
            if (!this.modal.show) {
                return;
            }

            this.modal.closeable = true;
            this.$nextTick(() => {
                this.modal.close = true;
            });
        },

        /**
         * @param {boolean} status
         *
         * @returns {void}
         */
        onCallback(status) {
            // If the popup modal was declined, we have to set the latest possible validity start date.
            if (!status) {
                const today = new Date();
                const date = new Date();
                date.setTime(today.getTime() + (this.limitValidityRangePicker * (3600 * 24 * 1000)));

                const da = new Intl.DateTimeFormat('de', { day: '2-digit' }).format(date);
                const mo = new Intl.DateTimeFormat('de', { month: '2-digit' }).format(date);
                const ye = new Intl.DateTimeFormat('de', { year: 'numeric' }).format(date);
                const formatted = (da + '.' + mo + '.' + ye).toString();

                this.$emit('declined', {
                    purchaseAbleDate: formatted,
                    jsDate: date,
                });

                return this.hideModal();
            }

            this.formErrors = {};
            const errors = [];

            if (typeof this.form.email !== 'string' || this.form.email.trim() === '') {
                errors.push(this.$t('shop.validityRangeLimitedMissingEmail'));
            } else if ((this.form.email.match(/^[\w-.]+@([\w-]+\.)+[\w-]{1,63}$/g) || []).length === 0) {
                errors.push(this.$t('shop.validityRangeLimitedInvalidEmail'));
            }

            if (errors.length > 0) {
                this.formErrors = {
                    email: errors,
                };

                return;
            }

            // Avoid the submitting of multiple requests.
            if (this.isOngoing) {
                return;
            }

            this.isOngoing = true;
            axios.post('/' + this.$language + '/payment/notification', {
                productType: this.productType,
                optionId: this.optionId,
                wishedDate: this.form.placeholders.wishedDate,
                orderAbleAtDate: this.form.placeholders.orderAbleAtDate,
                email: this.form.email,
                locale: this.$language,
            }).then((response) => {
                if (!response.data.success) {
                    this.formErrors = response.data.formErrors;

                    return;
                }

                this.successfulCompleted = true;
            }).then(() => {
                this.isOngoing = false;
            });
        },
    },
};
</script>

<style lang="scss">
#limit-validity-start-modal {
    .loader-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999999;

        background: rgba(255, 255, 255, 0.8);
    }
    .loader-container .preloader .status {
        .spinner {
            position: relative;
            text-align: center;

            .spinner-inner {
                position: relative;
                margin: auto auto 1rem;

                .double-bounce1,
                .double-bounce2 {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;

                    background-color: #2f55d4;
                    opacity: 0.6;

                    -webkit-animation: sk-bounce 2s infinite ease-in-out;
                    animation: sk-bounce 2s infinite ease-in-out;
                }

                .double-bounce2 {
                    -webkit-animation-delay: -1s;
                    animation-delay: -1s;
                }

                width: 2.5rem;
                height: 2.5rem;
            }

            .spinner-text {
                font-weight: bolder;
            }
        }

        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        -webkit-transform: translateY(-50%);
        transform: translateY(-50%);
    }
}
</style>
